import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '..\\layouts\\error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_passwordprotect_6705382b from 'nuxt_plugin_passwordprotect_6705382b' // Source: .\\password-protect.js (mode: 'all')
import nuxt_plugin_axios_ad5cd520 from 'nuxt_plugin_axios_ad5cd520' // Source: .\\axios.js (mode: 'all')
import nuxt_plugin_nuxtvuexroutersync_6e51fe58 from 'nuxt_plugin_nuxtvuexroutersync_6e51fe58' // Source: .\\nuxt-vuex-router-sync.js (mode: 'all')
import nuxt_plugin_vuewaitplugin_5a43c8bc from 'nuxt_plugin_vuewaitplugin_5a43c8bc' // Source: .\\vue-wait-plugin.js (mode: 'all')
import nuxt_plugin_settings_33f95e36 from 'nuxt_plugin_settings_33f95e36' // Source: .\\settings.js (mode: 'all')
import nuxt_plugin_vuedi_2de8767c from 'nuxt_plugin_vuedi_2de8767c' // Source: .\\vue-di.js (mode: 'all')
import nuxt_plugin_vuexpersistclient_3ecc3cfa from 'nuxt_plugin_vuexpersistclient_3ecc3cfa' // Source: ..\\plugins\\vuex-persist.client.ts (mode: 'client')
import nuxt_plugin_ie_3498324a from 'nuxt_plugin_ie_3498324a' // Source: ..\\plugins\\ie.ts (mode: 'all')
import nuxt_plugin_mobiledetectclient_0c2ae78c from 'nuxt_plugin_mobiledetectclient_0c2ae78c' // Source: ..\\plugins\\mobile-detect.client.ts (mode: 'client')
import nuxt_plugin_components_50cb0ca1 from 'nuxt_plugin_components_50cb0ca1' // Source: ..\\plugins\\components.ts (mode: 'all')
import nuxt_plugin_validation_7d99af44 from 'nuxt_plugin_validation_7d99af44' // Source: ..\\plugins\\validation.ts (mode: 'all')
import nuxt_plugin_rx_3389451e from 'nuxt_plugin_rx_3389451e' // Source: ..\\plugins\\rx.ts (mode: 'all')
import nuxt_plugin_di_35217a5c from 'nuxt_plugin_di_35217a5c' // Source: ..\\plugins\\di.ts (mode: 'all')
import nuxt_plugin_vueclient_dce95d38 from 'nuxt_plugin_vueclient_dce95d38' // Source: ..\\plugins\\vue.client.ts (mode: 'client')
import nuxt_plugin_aiclient_5b87d09c from 'nuxt_plugin_aiclient_5b87d09c' // Source: ..\\plugins\\ai.client.ts (mode: 'client')
import nuxt_plugin_authentication_d7e6b602 from 'nuxt_plugin_authentication_d7e6b602' // Source: ..\\plugins\\authentication.ts (mode: 'all')
import nuxt_plugin_moment_57893b12 from 'nuxt_plugin_moment_57893b12' // Source: ..\\plugins\\moment.ts (mode: 'all')
import nuxt_plugin_vuetifydev_70777e0d from 'nuxt_plugin_vuetifydev_70777e0d' // Source: ..\\plugins\\vuetify-dev.ts (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn(`<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead`)
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>`
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [ defaultTransition ],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [ transitions ]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        const nuxt = this.nuxt || this.$options.nuxt
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error('inject(key, value) has no value provided')
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Vue.prototype.hasOwnProperty(key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (typeof nuxt_plugin_passwordprotect_6705382b === 'function') {
    await nuxt_plugin_passwordprotect_6705382b(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_ad5cd520 === 'function') {
    await nuxt_plugin_axios_ad5cd520(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtvuexroutersync_6e51fe58 === 'function') {
    await nuxt_plugin_nuxtvuexroutersync_6e51fe58(app.context, inject)
  }

  if (typeof nuxt_plugin_vuewaitplugin_5a43c8bc === 'function') {
    await nuxt_plugin_vuewaitplugin_5a43c8bc(app.context, inject)
  }

  if (typeof nuxt_plugin_settings_33f95e36 === 'function') {
    await nuxt_plugin_settings_33f95e36(app.context, inject)
  }

  if (typeof nuxt_plugin_vuedi_2de8767c === 'function') {
    await nuxt_plugin_vuedi_2de8767c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexpersistclient_3ecc3cfa === 'function') {
    await nuxt_plugin_vuexpersistclient_3ecc3cfa(app.context, inject)
  }

  if (typeof nuxt_plugin_ie_3498324a === 'function') {
    await nuxt_plugin_ie_3498324a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_mobiledetectclient_0c2ae78c === 'function') {
    await nuxt_plugin_mobiledetectclient_0c2ae78c(app.context, inject)
  }

  if (typeof nuxt_plugin_components_50cb0ca1 === 'function') {
    await nuxt_plugin_components_50cb0ca1(app.context, inject)
  }

  if (typeof nuxt_plugin_validation_7d99af44 === 'function') {
    await nuxt_plugin_validation_7d99af44(app.context, inject)
  }

  if (typeof nuxt_plugin_rx_3389451e === 'function') {
    await nuxt_plugin_rx_3389451e(app.context, inject)
  }

  if (typeof nuxt_plugin_di_35217a5c === 'function') {
    await nuxt_plugin_di_35217a5c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueclient_dce95d38 === 'function') {
    await nuxt_plugin_vueclient_dce95d38(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_aiclient_5b87d09c === 'function') {
    await nuxt_plugin_aiclient_5b87d09c(app.context, inject)
  }

  if (typeof nuxt_plugin_authentication_d7e6b602 === 'function') {
    await nuxt_plugin_authentication_d7e6b602(app.context, inject)
  }

  if (typeof nuxt_plugin_moment_57893b12 === 'function') {
    await nuxt_plugin_moment_57893b12(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetifydev_70777e0d === 'function') {
    await nuxt_plugin_vuetifydev_70777e0d(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
